import { memo } from "react";
import type { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PackageOpen, StickyNote, Trash2, CalendarDays, PlusCircle, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import { Item, NewItem } from "@/types/purchase";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { OrderControls, OrderStatus } from "@/components/OrderControls";
import { RecentItemsSection } from "@/components/RecentItemsSection";
import { AddProductForm } from "@/components/AddProductForm";
import { ProductList } from "@/components/ProductList";
import type { BulkApplyTargets } from "@/hooks/useSameProductBulkEdit";
import type { OnboardingStep } from "@/hooks/useOnboarding";
import type { ShippingMode } from "@/hooks/useShippingFeeMode";
import type { QuickAddStockByNorm } from "@/hooks/useInventoryQuickAdd";
import { BlacklistToggleButton } from "@/components/BlacklistToggleButton";
import type { BlacklistEntry } from "@/supabase/blacklist/types/blacklist";
import type { BlacklistStatus } from "@/hooks/useBlacklist";

// 배송비 모드 토글 — Home.tsx에서 옮겨온 동일 컴포넌트.
// 두 곳(Home.tsx, ActiveCustomerColumn.tsx)에서 import 가능하도록 export.
export const ShippingModeToggle = memo(function ShippingModeToggle({
  shippingMode,
  onChange,
  jejuShippingFee,
  shippingPresets,
}: {
  shippingMode: ShippingMode;
  onChange: (mode: ShippingMode) => void;
  jejuShippingFee: number;
  shippingPresets: number[];
}) {
  return (
    <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-2 py-1 shadow-sm">
      <span className="text-xs font-medium text-indigo-700">배송비:</span>
      <ToggleGroup
        type="single"
        value={shippingMode}
        onValueChange={(value) => {
          if (value) onChange(value as ShippingMode);
        }}
        className="h-7"
      >
        <ToggleGroupItem
          value="auto"
          aria-label="자동"
          className="text-xs h-7 px-2 rounded-sm transition-colors text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-gray-800"
          title="기본 배송비 적용(기준금액 도달 시 0원)"
        >
          자동
        </ToggleGroupItem>
        {shippingPresets.map((amount) => (
          <ToggleGroupItem
            key={amount}
            value={`preset:${amount}`}
            aria-label={`배송비 ${amount.toLocaleString()}원`}
            className="text-xs h-7 px-2 rounded-sm transition-colors text-sky-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-sky-800"
            title={`배송비 ${amount.toLocaleString()}원 적용`}
          >
            {amount.toLocaleString()}
          </ToggleGroupItem>
        ))}
        <ToggleGroupItem
          value="jeju"
          aria-label="제주/산간"
          className="text-xs h-7 px-2 rounded-sm transition-colors text-indigo-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-800"
          title={`제주/산간 배송비 적용 (${jejuShippingFee.toLocaleString()}원)`}
        >
          제주
        </ToggleGroupItem>
        <ToggleGroupItem
          value="free"
          aria-label="무료배송"
          className="text-xs h-7 px-2 rounded-sm transition-colors text-emerald-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-emerald-800"
          title="항상 0원 처리"
        >
          무료
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
});

// 결제수단(현금/카드) 토글 — opt-in 셀러의 order_ 고객에게만 렌더된다.
// ToggleGroup type="single"은 같은 값 재클릭 시 ''를 넘김 = 해제(미기재) 동작이 공짜로 따라옴.
export const PaymentMethodToggle = memo(function PaymentMethodToggle({
  customerId,
  value,
  onChange,
}: {
  customerId: string;
  value: 'cash' | 'card' | null;
  onChange: (customerId: string, next: 'cash' | 'card' | null) => void;
}) {
  return (
    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-2 py-1 shadow-sm">
      <span className="text-xs font-medium text-emerald-700">결제:</span>
      <ToggleGroup
        type="single"
        value={value ?? ''}
        onValueChange={(v) => {
          onChange(customerId, v === 'cash' || v === 'card' ? v : null);
        }}
        className="h-7"
      >
        <ToggleGroupItem
          value="cash"
          aria-label="현금"
          className="text-xs h-7 px-2 rounded-sm transition-colors text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-emerald-800"
          title="현금(계좌이체 포함)으로 기재"
        >
          💵 현금
        </ToggleGroupItem>
        <ToggleGroupItem
          value="card"
          aria-label="카드"
          className="text-xs h-7 px-2 rounded-sm transition-colors text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-emerald-800"
          title="카드로 기재"
        >
          💳 카드
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
});

export type ActiveCustomerColumnVariant = "xl" | "lg" | "mobile";

export interface ActiveCustomerColumnProps {
  variant: ActiveCustomerColumnVariant;
  titleStyle: string;
  // auth/customer
  user: User | null;
  activeCustomer: CustomerTemplate | null | undefined;
  activeCustomerId: string | null;
  // items + math
  currentItems: Item[];
  totalAmount: number;
  calculatedShippingFee: number;
  paymentAmount: number;
  // item handlers
  handleItemsChangeAndPersist: (itemsOrUpdater: Item[] | ((prev: Item[]) => Item[])) => void;
  handleAddItem: (payload: NewItem) => Promise<boolean>;
  handleRecentItemAdd: (item: Item) => void | Promise<void>;
  handleRemoveItemByIndex: (index: number) => Promise<void> | void;
  handleUpdateItemQuantity: (index: number, q: number) => void;
  handleUpdateItem: (index: number, data: Partial<Item>) => Promise<void> | void;
  // 동일 상품 일괄 수정(opt-in 체크박스) — optional: 미주입 시 ProductList는 기존과 100% 동일
  getBulkApplyTargets?: (item: Item) => BulkApplyTargets;
  handleUpdateItemWithSameProducts?: (index: number, data: Partial<Item>) => Promise<void> | void;
  resetOrder: () => void;
  // OrderControls
  isOrderKept: boolean;
  currentOrderStatus: OrderStatus;
  handleKeepOrderChange: (checked: boolean | "indeterminate") => void;
  handleOrderStatusChange: (status: OrderStatus) => void;
  // 스펙 D10: 계좌 연동(linked) 셀러만 '부분 입금'을 선택할 수 있다(Home의 selectableOrderStatuses 결과 주입).
  statusOptions: OrderStatus[];
  // ShippingModeToggle
  shippingMode: ShippingMode;
  handleShippingModeChange: (mode: ShippingMode) => void;
  jejuShippingFee: number;
  shippingPresets: number[];
  // PaymentMethodToggle (opt-in)
  paymentMethodEnabled: boolean;
  handlePaymentMethodChange: (customerId: string, next: 'cash' | 'card' | null) => void;
  // date dialog
  openMoveOrderDateDialog: () => void;
  // memo
  isEditingMemo: boolean;
  setIsEditingMemo: (v: boolean) => void;
  memoInput: string;
  setMemoInput: (v: string) => void;
  isMemoPopoverOpen: boolean;
  setIsMemoPopoverOpen: (v: boolean) => void;
  isMemoPopoverOpenCompact: boolean;
  setIsMemoPopoverOpenCompact: (v: boolean) => void;
  handleCustomerMemoChange: (id: string, memo: string) => Promise<boolean>;
  handleMemoKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  focusInlineMemoEnd: () => void;
  inlineMemoRef: React.RefObject<HTMLTextAreaElement>;
  popoverMemoRef: React.RefObject<HTMLTextAreaElement>;
  // catalog
  productCatalog: Item[];
  isCatalogLoading: boolean;
  deleteCatalogItem: (itemName: string) => void;
  clearCatalog: () => void;
  // PR6 재고 quick-add — optional pass-through: 미주입/null 시 RecentItemsSection은 기존과
  // 100% 동일(동일 상품 일괄 수정 optional prop 선례). 참조는 useInventoryQuickAdd가 안정
  // 보장(성공 fetch + 값 변경 시에만 교체)이라 memo 계약 안전.
  inventoryMasterItems?: Item[] | null;
  inventoryStockByNorm?: QuickAddStockByNorm | null;
  // onboarding
  onboardingActive: boolean;
  onboardingStep: OnboardingStep;
  // 거파(블랙리스트) — optional pass-through: 미주입 시 기존과 100% 동일(inventoryMasterItems 선례).
  // 참조 안정은 useBlacklist가 보장(entries는 변경 시에만 교체, 콜백은 useCallback) — memo 계약 안전.
  blacklistEntry?: BlacklistEntry;
  blacklistEntries?: BlacklistEntry[];
  blacklistStatus?: BlacklistStatus;
  onBlacklistRegister?: (nickname: string, reason: string) => Promise<boolean>;
  onBlacklistUnregister?: (nickname: string) => Promise<boolean>;
  // login modal
  openLoginModal: () => void;
}

function ActiveCustomerColumnImpl(props: ActiveCustomerColumnProps) {
  const {
    variant,
    titleStyle,
    user,
    activeCustomer,
    activeCustomerId,
    currentItems,
    totalAmount,
    calculatedShippingFee,
    paymentAmount,
    handleItemsChangeAndPersist,
    handleAddItem,
    handleRecentItemAdd,
    handleRemoveItemByIndex,
    handleUpdateItemQuantity,
    handleUpdateItem,
    getBulkApplyTargets,
    handleUpdateItemWithSameProducts,
    resetOrder,
    isOrderKept,
    currentOrderStatus,
    handleKeepOrderChange,
    handleOrderStatusChange,
    statusOptions,
    shippingMode,
    handleShippingModeChange,
    jejuShippingFee,
    shippingPresets,
    paymentMethodEnabled,
    handlePaymentMethodChange,
    openMoveOrderDateDialog,
    isEditingMemo,
    setIsEditingMemo,
    memoInput,
    setMemoInput,
    isMemoPopoverOpen,
    setIsMemoPopoverOpen,
    isMemoPopoverOpenCompact,
    setIsMemoPopoverOpenCompact,
    handleCustomerMemoChange,
    handleMemoKeyDown,
    focusInlineMemoEnd,
    inlineMemoRef,
    popoverMemoRef,
    productCatalog,
    isCatalogLoading,
    deleteCatalogItem,
    clearCatalog,
    inventoryMasterItems,
    inventoryStockByNorm,
    onboardingActive,
    onboardingStep,
    blacklistEntry,
    blacklistEntries,
    blacklistStatus,
    onBlacklistRegister,
    onBlacklistUnregister,
    openLoginModal,
  } = props;

  // 변형별 차이 분기:
  // - XL: isMemoPopoverOpen / "inline-memo-xl" / 첫 상품 카드 폴백 표시 / AddProductForm.hasItems 전달 / "킵/주문상태" 안내
  // - LG: isMemoPopoverOpenCompact / "inline-memo-compact" / 폴백 없음 / hasItems 미전달 / "킵/주문상태" 안내
  // - MOBILE: isMemoPopoverOpenCompact / "inline-memo-compact-mobile" / 폴백 없음 / hasItems 미전달 / "킵/주문상태/배송비" 안내
  const popoverOpen = variant === "xl" ? isMemoPopoverOpen : isMemoPopoverOpenCompact;
  const setPopoverOpen = variant === "xl" ? setIsMemoPopoverOpen : setIsMemoPopoverOpenCompact;
  const otherPopoverSetter = variant === "xl" ? setIsMemoPopoverOpenCompact : setIsMemoPopoverOpen;
  const inlineMemoKey =
    variant === "xl"
      ? "inline-memo-xl"
      : variant === "lg"
        ? "inline-memo-compact"
        : "inline-memo-compact-mobile";
  const showFirstProductFallback = variant === "xl";
  const passHasItems = variant === "xl";
  const loggedOutMessage =
    variant === "mobile"
      ? "로그인하시면 킵/주문상태/배송비를 관리할 수 있습니다."
      : "로그인하시면 킵/주문상태를 관리할 수 있습니다.";
  // Mobile 변형에서 로그아웃 컨테이너 wrapper 클래스가 다름 (원본 보존)
  const loggedOutWrapperClass =
    variant === "mobile"
      ? "text-sm text-gray-600 flex items-center"
      : "flex items-center gap-2 flex-wrap";

  // Mobile은 title 아래 전체를 <div className="space-y-4">로 감싼다 (원본 구조).
  // XL/LG는 title → customer/memo block → <div className="space-y-4 mt-4"> {RecentItems...ProductList} 구조.
  const customerBlock = activeCustomer && (
    <div className="mt-1 space-y-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm text-gray-600 flex-shrink-0">선택된 고객:</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-semibold text-indigo-700 truncate">
              {activeCustomer.nickname}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{activeCustomer.nickname}</p>
          </TooltipContent>
        </Tooltip>
        {user && (
          <Popover
            open={popoverOpen}
            onOpenChange={(open) => {
              if (open) {
                otherPopoverSetter(false);
                setMemoInput((activeCustomer as any)?.memo || "");
              }
              setPopoverOpen(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-auto relative flex items-center gap-1 transition-colors rounded-md px-2 py-1 flex-shrink-0",
                  (activeCustomer as any)?.memo
                    ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    : "bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                )}
                disabled={!activeCustomer}
                aria-label="고객 메모 보기/편집"
              >
                <StickyNote className="h-4 w-4" />
                <span className="text-xs font-medium">메모</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" side="bottom" align="start">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">고객 메모</h4>
                  <p className="text-sm text-muted-foreground">
                    <strong>{activeCustomer.nickname}</strong> 님에 대한 메모입니다.
                  </p>
                </div>
                <Textarea
                  value={memoInput}
                  onChange={(e) => setMemoInput(e.target.value)}
                  placeholder={"여기에 메모를 입력하세요... \n(줄바꿈은 Shift + Enter)"}
                  className="min-h-[100px]"
                  onKeyDown={handleMemoKeyDown}
                  autoFocus
                  ref={popoverMemoRef}
                  onFocus={(e) => {
                    const el = e.currentTarget;
                    const end = el.value.length;
                    try {
                      el.setSelectionRange(end, end);
                    } catch {}
                  }}
                />
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm ${
                      memoInput.length > 300 ? "text-red-500" : "text-gray-500"
                    }`}
                  >
                    {memoInput.length} / 300
                  </span>
                  <Button
                    onClick={async () => {
                      if (activeCustomerId) {
                        const success = await handleCustomerMemoChange(
                          activeCustomerId,
                          memoInput
                        );
                        if (success) {
                          setIsMemoPopoverOpen(false);
                          setIsMemoPopoverOpenCompact(false);
                        }
                      } else {
                        setIsMemoPopoverOpen(false);
                        setIsMemoPopoverOpenCompact(false);
                      }
                    }}
                  >
                    메모 저장
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {user &&
          blacklistEntries &&
          blacklistStatus &&
          onBlacklistRegister &&
          onBlacklistUnregister &&
          activeCustomer.nickname !== "고객 닉네임" && (
            <BlacklistToggleButton
              key={`${user.id}:${activeCustomer.nickname}`}
              nickname={activeCustomer.nickname}
              entry={blacklistEntry}
              entries={blacklistEntries}
              status={blacklistStatus}
              onRegister={onBlacklistRegister}
              onUnregister={onBlacklistUnregister}
            />
          )}
      </div>
      {/* 거파 경고 배너 — 등록 고객 활성화 = 주문 처리 직전의 위험 순간이라 상시 노출.
          blacklistEntry 있을 때만 렌더(무마찰: 미등록/미주입 시 미존재). role=alert = 선택 시 스크린리더 즉시 안내. */}
      {blacklistEntry && (
        <div key={blacklistEntry.id} role="alert" className="flex items-start gap-1.5 p-2 bg-red-50 border-l-4 border-red-500 rounded-r-md">
          <Ban className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-red-700">거파 등록 고객입니다.</p>
            {blacklistEntry.reason && (
              <p className="text-xs text-red-900 whitespace-pre-wrap break-words">사유: {blacklistEntry.reason}</p>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-center flex-wrap gap-2">
        {user ? (
          <>
            <OrderControls
              activeCustomer={activeCustomer}
              isOrderKept={isOrderKept}
              onKeepOrderChange={handleKeepOrderChange}
              currentOrderStatus={currentOrderStatus}
              onOrderStatusChange={handleOrderStatusChange}
              className=""
              data-tour="order-controls"
              statusOptions={statusOptions}
            />
            <ShippingModeToggle
              shippingMode={shippingMode}
              onChange={handleShippingModeChange}
              jejuShippingFee={jejuShippingFee}
              shippingPresets={shippingPresets}
            />
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => openMoveOrderDateDialog()}
            >
              <CalendarDays className="h-4 w-4 mr-1" />
              날짜 변경
            </Button>
            {paymentMethodEnabled && activeCustomerId && activeCustomerId.startsWith('order_') && (
              <PaymentMethodToggle
                customerId={activeCustomerId}
                value={activeCustomer.paymentMethod ?? null}
                onChange={handlePaymentMethodChange}
              />
            )}
          </>
        ) : (
          <div className={loggedOutWrapperClass} data-tour="order-controls">
            {variant !== "mobile" && (
              <ShippingModeToggle
                shippingMode={shippingMode}
                onChange={handleShippingModeChange}
                jejuShippingFee={jejuShippingFee}
                shippingPresets={shippingPresets}
              />
            )}
            <div className="text-sm text-gray-600 flex items-center">
              <span className="mr-2">{loggedOutMessage}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openLoginModal()}
                className="text-xs h-7"
              >
                로그인
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const memoBlock =
    activeCustomer &&
    (((activeCustomer as any).memo && (activeCustomer as any).memo.length > 0) ||
      isEditingMemo) ? (
      <div
        className={
          variant === "mobile"
            ? "mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            : "mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
        }
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start mr-2 w-full">
            <StickyNote className="h-4 w-4 mr-2.5 mt-1 text-amber-600 flex-shrink-0" />
            {isEditingMemo ? (
              <Textarea
                key={inlineMemoKey}
                autoFocus
                value={memoInput}
                onFocus={(e) => {
                  const el = e.currentTarget;
                  const end = el.value.length;
                  try {
                    el.setSelectionRange(end, end);
                  } catch {}
                }}
                onChange={(e) => setMemoInput(e.target.value)}
                onBlur={() => {
                  if (activeCustomerId) {
                    handleCustomerMemoChange(activeCustomerId, memoInput);
                  }
                  setIsEditingMemo(false);
                }}
                ref={inlineMemoRef}
                className="text-sm w-full bg-amber-50 border-amber-300 focus:bg-white focus:ring-amber-500 resize-none"
                onKeyDown={handleMemoKeyDown}
              />
            ) : (
              <p
                className="text-sm text-amber-900 whitespace-pre-wrap break-words leading-relaxed w-full cursor-pointer"
                onClick={() => {
                  setMemoInput((activeCustomer as any)?.memo || "");
                  setIsEditingMemo(true);
                  focusInlineMemoEnd();
                }}
              >
                {(activeCustomer as any).memo}
              </p>
            )}
          </div>
          {!isEditingMemo && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-amber-500 hover:bg-amber-100 hover:text-amber-700 flex-shrink-0"
              onClick={() => {
                if (window.confirm("메모를 삭제하시겠습니까?")) {
                  if (activeCustomerId) {
                    handleCustomerMemoChange(activeCustomerId, "");
                  }
                }
              }}
              aria-label="메모 삭제"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    ) : null;

  const recentItemsBlock = showFirstProductFallback ? (
    (user || currentItems.length > 0) ? (
      <RecentItemsSection
        items={productCatalog}
        onItemClick={handleRecentItemAdd}
        isLoading={isCatalogLoading}
        onDeleteItem={deleteCatalogItem}
        onClearAllItems={clearCatalog}
        isHighlighting={onboardingActive && onboardingStep === "addItem"}
        inventoryMasterItems={inventoryMasterItems}
        inventoryStockByNorm={inventoryStockByNorm}
      />
    ) : (
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 text-center">
        <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
          <PlusCircle className="w-6 h-6 text-indigo-500" />
        </div>
        <h3 className="font-semibold text-indigo-900 mb-1">첫 상품을 추가해보세요</h3>
        <p className="text-sm text-indigo-600 mb-4">
          아래 입력창에 상품명과 가격을 입력하여<br />
          주문 목록에 추가할 수 있습니다.
        </p>
      </div>
    )
  ) : (user || currentItems.length > 0) ? (
    <RecentItemsSection
      items={productCatalog}
      onItemClick={handleRecentItemAdd}
      isLoading={isCatalogLoading}
      onDeleteItem={deleteCatalogItem}
      onClearAllItems={clearCatalog}
      isHighlighting={onboardingActive && onboardingStep === "addItem"}
      inventoryMasterItems={inventoryMasterItems}
      inventoryStockByNorm={inventoryStockByNorm}
    />
  ) : null;

  const addFormBlock = passHasItems ? (
    <AddProductForm onAddItem={handleAddItem} hasItems={currentItems.length > 0} />
  ) : (
    <AddProductForm onAddItem={handleAddItem} />
  );

  const productListBlock = (
    <>
      {currentItems.length > 0 && (
        <h3 className="text-sm font-medium text-gray-600 sticky top-0 bg-white py-1 z-10">
          현재 고객 상품 목록 ({currentItems.length}개)
        </h3>
      )}
      <ProductList
        items={currentItems}
        setItems={handleItemsChangeAndPersist}
        totalAmount={totalAmount}
        calculatedShippingFee={calculatedShippingFee}
        paymentAmount={paymentAmount}
        resetOrder={resetOrder}
        removeItemByIndex={handleRemoveItemByIndex}
        updateItemQuantity={handleUpdateItemQuantity}
        updateItem={handleUpdateItem}
        getBulkApplyTargets={getBulkApplyTargets}
        onSaveWithSameProducts={handleUpdateItemWithSameProducts}
        showResetButton={false}
      />
    </>
  );

  if (variant === "mobile") {
    // 원본 mobile 구조: <div className="space-y-4">로 customer/memo/items 전부 wrap.
    return (
      <div>
        <div className={`${titleStyle} flex items-center mb-4`}>
          <PackageOpen className="w-5 h-5 mr-2 text-indigo-600" />
          상품 추가 및 목록
        </div>
        <div className="space-y-4">
          {customerBlock}
          {memoBlock}
          {recentItemsBlock}
          {addFormBlock}
          {productListBlock}
        </div>
      </div>
    );
  }

  // XL/LG 구조: customer/memo는 wrapper 밖, items만 space-y-4 mt-4로 감싼다.
  return (
    <div>
      <div className={`${titleStyle} flex items-center mb-4`}>
        <PackageOpen className="w-5 h-5 mr-2 text-indigo-600" />
        상품 추가 및 목록
      </div>
      {customerBlock}
      {memoBlock}
      <div className="space-y-4 mt-4">
        {recentItemsBlock}
        {addFormBlock}
        {productListBlock}
      </div>
    </div>
  );
}

export const ActiveCustomerColumn = memo(ActiveCustomerColumnImpl);
